"use client";

import * as React from "react";
import { MessageCircle, Search as SearchIcon, History, PanelLeft, SquarePen, X } from "lucide-react";

import type { Source } from "@/components/api/fiinder-api-types";
import { Button } from "@/components/ui/button";
import { Chat } from "@/components/ui/chat/chat";
import { Search } from "@/components/ui/search/search";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { WidgetModeProvider, useWidgetMode } from "@/components/ui/widget/widget-mode-context";
import { useSearchContext } from "@/components/ui/search/search-context";
import { SearchProviderFromChat } from "@/components/ui/search/search-provider-from-chat";
import { useSourceNavigation } from "@/lib/use-source-navigation";
import { isPdfUrl } from "@/lib/url-utils";
import { useWidgetModeOptional } from "@/components/ui/widget/widget-mode-context";
import type { RecentPage } from "@/lib/recent-pages-storage";
import { usePersonation } from "@/lib/personation-context";
import { useIsMobile } from "@/lib/useIsMobile";
import { usePdfViewer } from "@/components/ui/pdf-viewer/pdf-context";
import { LandingPdfViewer, type LandingPdfViewerProps } from "./landing-pdf-viewer";
import { LandingSourcesSidecar } from "./landing-sources-sidecar";
import { SplitPanel } from "./split-panel";

interface LandingPageProps {
  logoUrl?: string;
  pdfViewerProps?: LandingPdfViewerProps;
}

type LandingSourcesSidecarData = {
  sources: Source[];
  queries?: string[];
};

const SIDEBAR_EXPANDED_WIDTH = 72;
const SIDEBAR_COLLAPSED_WIDTH = 64;
const MOBILE_COLLAPSED_WIDTH = 0;
const SPLIT_TAKEOVER_BREAKPOINT = 1100;

/**
 * Returns true when split-panel should be in takeover mode.
 * Keep this breakpoint aligned with split-panel.tsx.
 */
function useIsTakeoverMode() {
  const [isTakeover, setIsTakeover] = React.useState(() =>
    window.innerWidth < SPLIT_TAKEOVER_BREAKPOINT,
  );

  React.useEffect(() => {
    const onResize = () => {
      setIsTakeover(window.innerWidth < SPLIT_TAKEOVER_BREAKPOINT);
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return isTakeover;
}

/**
 * LandingSidebar - Left sidebar with logo and new chat button (Perplexity style)
 * When collapsed, only the logo remains visible.
 */
function LandingSidebar({
  logoUrl,
  collapsed,
  isMobile,
  forceHidden,
  onLogoClick,
}: {
  logoUrl?: string;
  collapsed: boolean;
  isMobile: boolean;
  forceHidden: boolean;
  onLogoClick: () => void;
}) {
  const { companyMainColor, labels } = usePersonation();
  const { mode, setMode } = useWidgetMode();
  const sidebarWidth = forceHidden
    ? 0
    : collapsed
    ? (isMobile ? MOBILE_COLLAPSED_WIDTH : SIDEBAR_COLLAPSED_WIDTH)
    : SIDEBAR_EXPANDED_WIDTH;
  const contentState = collapsed ? "collapsed" : "expanded";
  const shouldCollapseLogo = isMobile && collapsed;
  const logoClassName = isMobile ? "w-7 h-7 object-contain" : "w-10 h-10 object-contain";
  const navButtonClassName = isMobile
    ? "w-8 h-8 rounded-lg hover:bg-neutral-200"
    : "w-11 h-11 rounded-xl hover:bg-neutral-200";
  const navIconSize = isMobile ? 18 : 26;
  const animatedContentStyle: React.CSSProperties = {
    opacity: collapsed ? 0 : 1,
    pointerEvents: collapsed ? "none" : "auto",
    transform: collapsed ? "translateX(-8px) scale(0.84)" : "translateX(0) scale(1)",
    transition: "opacity 200ms ease-in-out, transform 200ms ease-in-out",
  };
  const logoWrapperStyle: React.CSSProperties = shouldCollapseLogo
    ? animatedContentStyle
    : {
        opacity: 1,
        pointerEvents: "auto",
        transform: "translateX(0) scale(1)",
        transition: "opacity 200ms ease-in-out, transform 200ms ease-in-out",
      };

  return (
    <aside
      className="h-full flex flex-col items-center py-4 shrink-0 overflow-hidden transition-all duration-200 ease-in-out"
      style={{
        width: sidebarWidth,
        backgroundColor: "transparent",
        transition: "width 200ms ease-in-out, background-color 300ms ease-in-out",
      }}
      data-testid="landing-sidebar"
    >
      <div
        className="flex items-center shrink-0 min-w-[72px] justify-center"
        style={logoWrapperStyle}
        data-testid="landing-logo-wrapper"
        data-state={contentState}
      >
        {logoUrl && (
          <button
            type="button"
            onClick={onLogoClick}
            aria-label={labels.landing.sidebar.labels.chat}
            data-testid="landing-logo-button"
            className="rounded-md transition-opacity hover:opacity-90"
          >
            <img
              src={logoUrl}
              alt="Logo"
              className={logoClassName}
              data-testid="landing-logo"
              aria-hidden={shouldCollapseLogo}
            />
          </button>
        )}
      </div>
      <div
        className="flex flex-col items-center gap-6 mt-8 transition-opacity duration-200 ease-in-out"
        style={animatedContentStyle}
        data-testid="landing-sidebar-actions"
        data-state={contentState}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={navButtonClassName}
              style={mode === "chat" ? { backgroundColor: `color-mix(in srgb, ${companyMainColor} 8%, transparent)` } : undefined}
              onClick={() => setMode("chat")}
              aria-label={labels.landing.sidebar.labels.chat}
              data-testid="landing-new-conversation"
            >
              <MessageCircle style={{ width: navIconSize, height: navIconSize, color: companyMainColor, opacity: mode === "chat" ? 0.5 : 0.7 }} strokeWidth={1.2} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right" showArrow={false} className="bg-neutral-100 text-neutral-500 text-[11px] font-normal shadow-sm border border-neutral-200">{labels.landing.sidebar.labels.chat}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={navButtonClassName}
              style={mode === "search" ? { backgroundColor: `color-mix(in srgb, ${companyMainColor} 8%, transparent)` } : undefined}
              onClick={() => setMode("search")}
              aria-label={labels.landing.sidebar.labels.search}
              data-testid="landing-search"
            >
              <SearchIcon style={{ width: navIconSize, height: navIconSize, color: companyMainColor, opacity: mode === "search" ? 0.5 : 0.7 }} strokeWidth={1.2} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right" showArrow={false} className="bg-neutral-100 text-neutral-500 text-[11px] font-normal shadow-sm border border-neutral-200">{labels.landing.sidebar.labels.search}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={navButtonClassName}
              aria-label={labels.landing.sidebar.labels.history}
              data-testid="landing-history"
            >
              <History style={{ width: navIconSize, height: navIconSize, color: companyMainColor, opacity: 0.7 }} strokeWidth={1.2} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right" showArrow={false} className="bg-neutral-100 text-neutral-500 text-[11px] font-normal shadow-sm border border-neutral-200">{labels.landing.sidebar.labels.history}</TooltipContent>
        </Tooltip>
      </div>
    </aside>
  );
}

/**
 * SidebarToggle - Discrete toggle button inspired by OpenAI's sidebar icon
 */
function SidebarToggle({
  onToggle,
}: {
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      className="p-1 rounded-md text-muted-foreground/50 hover:text-muted-foreground hover:bg-muted/50 transition-colors"
      aria-label="Toggle sidebar"
      data-testid="landing-sidebar-toggle"
    >
      <PanelLeft className="h-4.5 w-4.5" strokeWidth={1.2} />
    </button>
  );
}

/**
 * LandingSearchView - Custom search layout for landing page.
 * Places the search input at the top with results below in a scrollable area,
 * matching the centered column layout used by the chat mode.
 */
function LandingSearchView() {
  const context = useSearchContext();
  const widgetMode = useWidgetModeOptional();
  const { labels } = usePersonation();
  const { navigateToUrl } = useSourceNavigation();

  const handleRecentPageClick = React.useCallback(
    (page: RecentPage) => {
      navigateToUrl({
        url: page.url,
        fetchUrl: page.fetchUrl,
        title: page.title,
        description: page.description,
        keywords: page.keywords,
        og_image: page.og_image,
      });
    },
    [navigateToUrl]
  );

  const handleKeywordClick = React.useCallback(
    (keyword: string) => {
      widgetMode?.openSearchWithQuery(keyword);
    },
    [widgetMode]
  );

  const renderContent = () => {
    if (context.isLoading) {
      return <Search.Loading />;
    }

    if (!context.hasSearched) {
      return (
        <Search.RecentPages
          recentPages={context.recentPages}
          onPageClick={handleRecentPageClick}
          onKeywordClick={handleKeywordClick}
          title={labels.search.recentlyVisitedTitle}
        />
      );
    }

    if (context.results.length === 0) {
      return <Search.NoResults />;
    }

    return (
      <Search.Results
        results={context.results}
        activeIndex={context.activeIndex}
        onResultClick={context.handleResultClick}
        onKeywordClick={handleKeywordClick}
      />
    );
  };

  return (
    <div
      data-testid="landing-search-view"
      className="flex-1 min-h-0 flex flex-col overflow-hidden"
    >
      <div
        data-testid="search-scroll-container"
        className="flex-1 overflow-y-auto"
      >
        <div data-testid="search-content-column">
          <div className="pt-18 pb-6">
            <Search.Input clearIcon={<X className="h-5 w-5" />} />
          </div>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

/**
 * LandingBody - Main content area
 * Full width to allow scroll at page edge, content centered via CSS
 */
function LandingBody() {
  const { mode } = useWidgetMode();

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {mode === "chat" ? <Chat.Body /> : <LandingSearchView />}
    </div>
  );
}

/**
 * LandingFooter - Switches between Chat and Search footer based on mode
 */
function LandingFooter() {
  const { mode } = useWidgetMode();
  if (mode === "search") return null;
  return <Chat.Footer poweredByText={null} />;
}

/**
 * LandingPageContent - Inner content wrapped by providers
 */
function LandingPageContent({
  logoUrl,
  pdfViewerProps,
  sourcesSidecarData,
  setSourcesSidecarData,
}: {
  logoUrl?: string;
  pdfViewerProps?: LandingPdfViewerProps;
  sourcesSidecarData: LandingSourcesSidecarData | null;
  setSourcesSidecarData: React.Dispatch<React.SetStateAction<LandingSourcesSidecarData | null>>;
}) {
  const isMobile = useIsMobile();
  const isTakeoverMode = useIsTakeoverMode();
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState<boolean>(isMobile);
  const { mode, openSearchWithQuery, setMode } = useWidgetMode();
  const { companyMainColor } = usePersonation();
  const { navigateToSource } = useSourceNavigation();
  const chatContext = React.useContext(Chat.Context);
  const { currentPdf, isVisible, closePdf, openPdf } = usePdfViewer();
  const isPdfSidecarOpen = Boolean(isVisible && currentPdf);
  const isSourcesSidecarOpen = Boolean(sourcesSidecarData);
  const isSidecarOpen = isPdfSidecarOpen || isSourcesSidecarOpen;
  const isSidecarTakeoverOpen = isTakeoverMode && isSidecarOpen;

  // Ensure landing always starts with the PDF sidecar closed.
  React.useEffect(() => {
    closePdf();
  }, [closePdf]);

  React.useEffect(() => {
    if (sourcesSidecarData) {
      closePdf();
    }
  }, [sourcesSidecarData, closePdf]);

  React.useEffect(() => {
    if (mode !== "chat" && sourcesSidecarData) {
      setSourcesSidecarData(null);
    }
  }, [mode, sourcesSidecarData, setSourcesSidecarData]);

  React.useEffect(() => {
    if (isMobile) {
      setSidebarCollapsed(true);
    }
  }, [isMobile]);

  // DEV ONLY: expose openPdf for manual testing
  React.useEffect(() => {
    if (import.meta.env.DEV) {
      (window as any).__testOpenPdf = openPdf;
    }
    return () => { delete (window as any).__testOpenPdf; };
  }, [openPdf]);

  const currentSidebarWidth = isSidecarTakeoverOpen
    ? 0
    : sidebarCollapsed
    ? (isMobile ? MOBILE_COLLAPSED_WIDTH : SIDEBAR_COLLAPSED_WIDTH)
    : SIDEBAR_EXPANDED_WIDTH;

  const handleSourcesSidecarClose = React.useCallback(() => {
    setSourcesSidecarData(null);
  }, [setSourcesSidecarData]);

  const handleSidecarSourceClick = React.useCallback(
    (source: Source) => {
      // fetch_url's own suffix is the PDF signal -- never the public url's.
      const sourceIsPdf = Boolean(source.fetch_url) && isPdfUrl(source.fetch_url!);

      if (!sourceIsPdf) {
        setSourcesSidecarData(null);
      }

      navigateToSource({ source });
    },
    [navigateToSource, setSourcesSidecarData],
  );

  const handleSidecarQueryClick = React.useCallback(
    (query: string) => {
      setSourcesSidecarData(null);
      openSearchWithQuery(query);
    },
    [openSearchWithQuery, setSourcesSidecarData],
  );

  const sidecar = React.useMemo(() => {
    if (isPdfSidecarOpen && currentPdf) {
      return <LandingPdfViewer {...pdfViewerProps} />;
    }
    if (sourcesSidecarData) {
      return (
        <LandingSourcesSidecar
          sources={sourcesSidecarData.sources}
          queries={sourcesSidecarData.queries}
          onClose={handleSourcesSidecarClose}
          onSourceClick={handleSidecarSourceClick}
          onQueryClick={handleSidecarQueryClick}
        />
      );
    }
    return null;
  }, [
    isPdfSidecarOpen,
    currentPdf,
    pdfViewerProps,
    sourcesSidecarData,
    handleSourcesSidecarClose,
    handleSidecarSourceClick,
    handleSidecarQueryClick,
  ]);

  return (
    <div
      className="h-screen w-screen flex flex-row bg-background relative"
      style={{ "--intro-title-color": companyMainColor } as React.CSSProperties}
      data-testid="landing-page-container"
      data-mobile={isMobile ? "true" : "false"}
      data-sidebar-collapsed={sidebarCollapsed ? "true" : "false"}
      data-pdf-open={isSidecarOpen ? "true" : "false"}
    >
      <LandingSidebar
        logoUrl={logoUrl}
        collapsed={sidebarCollapsed}
        isMobile={isMobile}
        forceHidden={isSidecarTakeoverOpen}
        onLogoClick={() => {
          setMode("chat");
        }}
      />
      {!isSidecarTakeoverOpen && (
        <div
          className="absolute z-10"
          style={{
            left: currentSidebarWidth + 8,
            top: 25,
            transition: "left 200ms ease-in-out",
          }}
        >
          <SidebarToggle onToggle={() => setSidebarCollapsed((prev) => !prev)} />
        </div>
      )}
      <SplitPanel
        className="flex-1 min-h-0 min-w-0 overflow-hidden"
        sidecar={sidecar}
        isOpen={isSidecarOpen}
        onClose={isPdfSidecarOpen ? closePdf : handleSourcesSidecarClose}
        storageKey="landing_split_panel"
        defaultWidth={450}
        minWidth={300}
        maxWidthRatio={0.6}
      >
        <div className="relative flex-1 flex flex-col min-h-0">
          {mode === "chat" && !isSidecarTakeoverOpen && (
            <div
              className="absolute z-10"
              style={{ right: 12, top: 25 }}
            >
              <button
                onClick={chatContext?.handleNewRoom}
                className="p-1 rounded-md text-muted-foreground hover:bg-muted/50 transition-colors"
                aria-label="New chat"
                data-testid="landing-new-chat-toggle"
              >
                <SquarePen className="h-4.5 w-4.5" strokeWidth={1.2} />
              </button>
            </div>
          )}
          <LandingBody />
          <LandingFooter />
        </div>
      </SplitPanel>
    </div>
  );
}

/**
 * LandingPage - Full-page chat component with Perplexity-style sidebar
 * Renders sidebar + chat body + footer without header
 */
export function LandingPage({ logoUrl, pdfViewerProps }: LandingPageProps) {
  const [roomId, setRoomId] = React.useState<string | undefined>(undefined);
  const [sourcesSidecarData, setSourcesSidecarData] = React.useState<LandingSourcesSidecarData | null>(null);

  const handleOpenSources = React.useCallback(
    (sources: Source[], queries?: string[]) => {
      setSourcesSidecarData({ sources, queries });
      return true;
    },
    [],
  );

  return (
    <WidgetModeProvider onOpenSources={handleOpenSources}>
      <Chat.Provider
        roomId={roomId}
        onRoomIdChange={setRoomId}
        isFullScreen={true}
        renderWrapper={({ children }) => (
          <div data-testid="chat-container" className="h-full flex flex-col">
            {children}
          </div>
        )}
      >
        <SearchProviderFromChat>
          <LandingPageContent
            logoUrl={logoUrl}
            pdfViewerProps={pdfViewerProps}
            sourcesSidecarData={sourcesSidecarData}
            setSourcesSidecarData={setSourcesSidecarData}
          />
        </SearchProviderFromChat>
      </Chat.Provider>
    </WidgetModeProvider>
  );
}

LandingPage.displayName = "LandingPage";
LandingSidebar.displayName = "LandingSidebar";
LandingBody.displayName = "LandingBody";
LandingFooter.displayName = "LandingFooter";
LandingSearchView.displayName = "LandingSearchView";
